import React from 'react';

import { useSnackbar } from 'notistack';
import { Control, Controller, useWatch } from 'react-hook-form';

import { Autocomplete, Box, Button, Chip, Grid, MenuItem, Stack, TextField } from '@mui/material';

import { useSuggestTourCategories } from '~/hooks/apis/tours/useTourCategoriesSuggestion';

import {
  ACTIVITY_LEVEL_LABELS,
  GUIDE_TYPE_LABELS,
  TOUR_OFFER_FORMAT_LABELS,
  TOUR_OFFER_STATUS_LABELS,
  TOUR_STYLE_LABELS,
  TourOfferFormOptions,
} from '~/types/tours/tourOffer';

import NativeTourCard from './NativeTourCard';
import NativeTourEnumSelect from './NativeTourEnumSelect';
import { NativeTourForm } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
  options: TourOfferFormOptions | undefined;
  legacyTourId: string;
}

// Only a Trusted Partner tour names the partner that runs it, as on the tour form.
const TRUSTED_PARTNER_BRAND = 'letrustedpartnertours';

const nullIfEmpty = (value: string) => (value.trim() === '' ? null : value);

/** What the tour is and who sells it. */
export default function NativeTourOverviewSection({ control, options, legacyTourId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const suggest = useSuggestTourCategories(legacyTourId);
  const brand = useWatch({ control, name: 'brand' });
  const categories = options?.categories ?? [];

  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <NativeTourCard title="Tour" description="The name customers see, and whether the content is approved.">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Controller
                  control={control}
                  name="name"
                  rules={{ validate: (value) => !!value.trim() || 'Required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Name"
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <NativeTourEnumSelect
                      label="Status"
                      labels={TOUR_OFFER_STATUS_LABELS}
                      value={field.value}
                      onChange={field.onChange}
                      clearable={false}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </NativeTourCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <NativeTourCard
            title="Brand"
            description="Who the tour is sold as. Moving a tour to or from Luxury Escapes resets its fine print to that brand's default."
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="brand"
                  render={({ field }) => (
                    <TextField
                      select
                      label="Brand"
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value || null)}
                      disabled={!options}
                      fullWidth
                    >
                      {(options?.brands ?? []).map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              {brand === TRUSTED_PARTNER_BRAND && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    control={control}
                    name="content.operatedBy"
                    render={({ field }) => (
                      <TextField
                        label="Operated by"
                        helperText="The partner customers see running the tour"
                        value={field.value ?? ''}
                        onChange={(event) => field.onChange(nullIfEmpty(event.target.value))}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </NativeTourCard>
        </Grid>
      </Grid>

      <NativeTourCard title="Tour details" description="How the tour is described and filtered on the site.">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              control={control}
              name="content.tourStyle"
              render={({ field }) => (
                <TextField
                  label="Tour style"
                  value={field.value ? TOUR_STYLE_LABELS[field.value] : 'Not set'}
                  helperText="From Salesforce"
                  disabled
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              control={control}
              name="content.format"
              render={({ field }) => (
                <TextField
                  label="Format"
                  value={TOUR_OFFER_FORMAT_LABELS[field.value]}
                  helperText="From Salesforce"
                  disabled
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              control={control}
              name="content.guideType"
              render={({ field }) => (
                <NativeTourEnumSelect
                  label="Guide type"
                  labels={GUIDE_TYPE_LABELS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              control={control}
              name="content.activityLevel"
              render={({ field }) => (
                <NativeTourEnumSelect
                  label="Activity level"
                  labels={ACTIVITY_LEVEL_LABELS}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              control={control}
              name="content.categories"
              render={({ field }) => {
                const unknown = field.value.filter((category) => !categories.includes(category));
                const suggestCategories = async () => {
                  try {
                    const suggested = await suggest.mutateAsync();
                    if (suggested.length === 0) {
                      enqueueSnackbar("Couldn't suggest any categories", { variant: 'warning' });
                      return;
                    }
                    field.onChange(suggested);
                    enqueueSnackbar('Categories suggested from the content. Check them before saving.', {
                      variant: 'info',
                    });
                  } catch (error) {
                    enqueueSnackbar(`Couldn't suggest categories: ${error.message}`, { variant: 'error' });
                  }
                };
                return (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
                    <Box flexGrow={1}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={categories}
                        value={field.value}
                        onChange={(_event, next) => field.onChange(next)}
                        renderTags={(values, getTagProps) =>
                          values.map((value, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={value}
                              size="small"
                              label={value}
                              color={categories.length > 0 && unknown.includes(value) ? 'warning' : 'default'}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Categories"
                            helperText={
                              categories.length > 0 && unknown.length > 0
                                ? `Not a tour category any more: ${unknown.join(', ')}. Remove it or leave it as it is.`
                                : 'Where the tour shows up when customers filter by category'
                            }
                          />
                        )}
                      />
                    </Box>
                    <Button
                      variant="text"
                      disabled={suggest.isPending}
                      onClick={suggestCategories}
                      sx={{ mt: { md: 1 } }}
                    >
                      {suggest.isPending ? 'Suggesting…' : 'Suggest'}
                    </Button>
                  </Stack>
                );
              }}
            />
          </Grid>
        </Grid>
      </NativeTourCard>
    </Stack>
  );
}
